import { Lambda } from "@aws-sdk/client-lambda";
import path from "path-browserify";
import React, { useRef, useState } from "react";
import shortid from "shortid";
import styled from "styled-components";

import { gql } from "@examspark/api";
import { allowedFileUploadImageMimeTypes } from "@examspark/common/dist/utils/file";
import {
  Avatar,
  Button,
  Dropzone,
  HSpace,
  Text,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import {
  IconSearchLine,
  IconTrashLine,
} from "@examspark/common-ui/dist/components/icons";
import { User } from "@examspark/types";

import { uploadFile } from "../../util";
import createAwsClient from "../../utils/createAwsClient";
import { awsconfig } from "../../utils/util";

const AvatarWrapper = styled.div`
  border: dashed 2px #ccc;
  padding: 2rem;
`;

const StyledInner = styled(HSpace)`
  align-items: center;
  gap: 3rem;
`;
const StyledPrompt = styled(VSpace)``;

export const getUserBranchFromAWSConfig = () => {
  const env = awsconfig.aws_user_files_s3_bucket.match(/-([a-z]+)$/);
  return env?.[1] || "";
};

const ManageProfilePicture = (props: {
  onChange?: (hash: string | null) => void;
  user?: User;
}) => {
  const uploadButtonRef = useRef<React.ElementRef<"input">>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleClearPhoto = async () => {
    if (props.user == null) return;

    if (!window.confirm("Are you sure you want to clear your profile picture?"))
      return;

    setIsLoading(true);

    await gql.operations.user.updateUserAccount({
      id: props.user.id,
      sk: "user",
      avatarHash: null,
    });

    setIsLoading(false);
    props.onChange?.(null);
  };

  // console.log("user", props.user);

  const handleDrop = async (files: File[]) => {
    if (!props.user?.id) {
      console.error("No user");
      return;
    }

    setIsLoading(true);

    console.log("files", files);
    if (files.length === 0) {
      alert(
        "The file you selected for upload is invalid. Please try a valid file type.",
      );
      setIsLoading(false);
      return;
    }
    const file = files[0];

    // upload raw file
    const parsedPath = path.parse(file.name);
    const key = `avatars/${props.user.id}/avatar${parsedPath.ext}`;
    await uploadFile(key, file, {
      isTemporary: true,
    });

    // generate thumbnails
    const payload = {
      Records: [
        {
          srcFile: `uploads/${key}`,
          dstFolder: `public/avatars/${props.user.id}`,
          sizes: {
            s: [500],
            w: [1000],
          },
        },
      ],
    };

    const branch = getUserBranchFromAWSConfig();

    const lambdaThumbnailResult = await createAwsClient(Lambda).invoke({
      FunctionName: `HandleImageThumbnail-${branch}`,
      InvocationType: "RequestResponse",
      Payload: JSON.stringify(payload),
    });

    const avatarHash = shortid.generate();

    await gql.operations.user.updateUserAccount({
      id: props.user?.id,
      sk: "user",
      avatarHash,
    });

    setIsLoading(false);
    props.onChange?.(avatarHash);
    if (uploadButtonRef.current) {
      uploadButtonRef.current.value = "";
    }
    return lambdaThumbnailResult;
  };

  return (
    <AvatarWrapper>
      <Dropzone
        multiple={false}
        browseButtonRef={uploadButtonRef}
        disabled={isLoading}
        onDrop={handleDrop}
        allowedFileTypes={allowedFileUploadImageMimeTypes}
      >
        <StyledInner>
          <Avatar awsconfig={awsconfig} user={props.user} size="100" />
          <StyledPrompt>
            <Text.Body>
              Drag an image file into this area to upload. Or click the Browse
              button.
            </Text.Body>
            <div>
              <Toolbar>
                <Button
                  onClick={() => uploadButtonRef.current?.click()}
                  title="Select Picture"
                  variant="outlined"
                  disabled={isLoading}
                  isLoading={isLoading}
                  icon={<IconSearchLine />}
                >
                  Select Picture from Device
                </Button>
                <Button
                  onClick={() => handleClearPhoto()}
                  title="Clear Picture"
                  variant="outlined"
                  disabled={!props.user?.avatarHash || isLoading}
                  isLoading={isLoading}
                  icon={<IconTrashLine />}
                >
                  Clear Picture
                </Button>
              </Toolbar>
            </div>
          </StyledPrompt>
        </StyledInner>
      </Dropzone>
    </AvatarWrapper>
  );
};

export default ManageProfilePicture;
